import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import openaiService from '../services/openaiService';

const { FiSettings, FiLoader, FiCheck } = FiIcons;

const SmartRecommendations = () => {
  const [preferences, setPreferences] = useState({
    budget: '',
    vehicleType: '',
    usage: '',
    features: []
  });
  const [recommendations, setRecommendations] = useState('');
  const [loading, setLoading] = useState(false);

  const vehicleTypes = ['SUV', 'Sedan', 'No Preference'];
  const usageTypes = ['Daily Commuting', 'Family Transport', 'Luxury Driving', 'Business Use'];
  const featureOptions = ['All-Wheel Drive', 'Premium Sound', 'Advanced Safety', 'Sport Package', 'Technology Package'];

  const handleFeatureToggle = (feature) => {
    setPreferences(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const generateRecommendations = async () => {
    if (!preferences.budget || !preferences.vehicleType || !preferences.usage) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const result = await openaiService.generateVehicleRecommendations(preferences);
      setRecommendations(result);
    } catch (error) {
      setRecommendations('Unable to generate recommendations at the moment. Our sales team can provide personalized recommendations - please visit or call us!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6 border border-gray-700"
    >
      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
        <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2 text-audi-red" />
        Smart Vehicle Recommendations
      </h3>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Budget Range *
          </label>
          <select
            value={preferences.budget}
            onChange={(e) => setPreferences({...preferences, budget: e.target.value})}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-audi-red"
          >
            <option value="">Select your budget</option>
            <option value="Under $45,000">Under $45,000</option>
            <option value="$45,000 - $60,000">$45,000 - $60,000</option>
            <option value="$60,000 - $75,000">$60,000 - $75,000</option>
            <option value="Over $75,000">Over $75,000</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Vehicle Type *
          </label>
          <div className="grid grid-cols-3 gap-2">
            {vehicleTypes.map((type) => (
              <button
                key={type}
                onClick={() => setPreferences({...preferences, vehicleType: type})}
                className={`p-2 rounded-lg border transition-colors ${
                  preferences.vehicleType === type
                    ? 'bg-audi-red border-audi-red text-white'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Primary Usage *
          </label>
          <div className="grid grid-cols-2 gap-2">
            {usageTypes.map((usage) => (
              <button
                key={usage}
                onClick={() => setPreferences({...preferences, usage})}
                className={`p-2 rounded-lg border transition-colors ${
                  preferences.usage === usage
                    ? 'bg-audi-red border-audi-red text-white'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                }`}
              >
                {usage}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Desired Features (Optional)
          </label>
          <div className="grid grid-cols-2 gap-2">
            {featureOptions.map((feature) => (
              <button
                key={feature}
                onClick={() => handleFeatureToggle(feature)}
                className={`p-2 rounded-lg border transition-colors flex items-center justify-between ${
                  preferences.features.includes(feature)
                    ? 'bg-audi-red border-audi-red text-white'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                }`}
              >
                <span className="text-sm">{feature}</span>
                {preferences.features.includes(feature) && (
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={generateRecommendations}
        disabled={loading}
        className="w-full bg-audi-red hover:bg-red-700 disabled:bg-gray-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
      >
        {loading ? (
          <>
            <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
            Generating Recommendations...
          </>
        ) : (
          'Get AI Recommendations'
        )}
      </button>

      {recommendations && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 bg-gray-700 rounded-lg"
        >
          <h4 className="font-bold text-white mb-3">AI Recommendations</h4>
          <div className="text-gray-200 whitespace-pre-wrap text-sm leading-relaxed">
            {recommendations}
          </div>
          <button className="mt-4 w-full bg-gray-600 hover:bg-gray-500 text-white py-2 px-4 rounded-lg transition-colors">
            View Recommended Vehicles
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SmartRecommendations;